import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import type { Amp, ChatMessage } from '../features/chat/chat.types';
import * as ampService from '../services/ampService';

interface AmpMessages {
  [ampId: string]: ChatMessage[];
}

interface AmpContextValue {
  amps: Amp[];
  premadeAmps: Amp[];
  customAmps: Amp[];
  loading: boolean;
  initialLoading: boolean;
  error: string | null;
  getAmp: (id: string) => Amp | undefined;
  getAmpMessages: (ampId: string) => ChatMessage[];
  addMessage: (ampId: string, message: ChatMessage) => void;
  createAmp: (amp: Partial<Amp>) => Promise<Amp>;
  updateAmp: (id: string, amp: Partial<Amp>) => Promise<Amp>;
  deleteAmp: (id: string) => Promise<void>;
  fetchPremadeAmps: () => Promise<void>;
  fetchCustomAmps: () => Promise<void>;
  /** Loads premade + custom AMPs once per session (concurrent callers share the
   *  request). Call it where the lists are actually needed — nothing loads them
   *  up front any more. */
  ensureAmpsLoaded: () => Promise<void>;
  addCustomAmp: (amp: Amp) => void;
  removeCustomAmp: (ampId: string) => void;
}

const AmpContext = createContext<AmpContextValue | null>(null);

export const useAmps = (): AmpContextValue => {
  const ctx = useContext(AmpContext);
  if (!ctx) throw new Error('useAmps must be used within AmpProvider');
  return ctx;
};

/** useAmps() for screens that need the AMP lists as soon as they mount. */
export const useAmpsLoaded = (): AmpContextValue => {
  const ctx = useAmps();
  const { ensureAmpsLoaded } = ctx;
  useEffect(() => { void ensureAmpsLoaded(); }, [ensureAmpsLoaded]);
  return ctx;
};

const getDefaultAmpMessages = (amp: Amp | undefined, ampId: string): ChatMessage[] => {
  const ampName = amp?.name || ampId;
  return [
    {
      id: `${ampId}-welcome`,
      role: 'assistant',
      content: `Welcome to **${ampName}**! ${amp?.description || 'How can I help you today?'}`,
      timestamp: new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
    },
  ];
};

const SUPPORT_ONLY_AMP_NAMES = new Set(['Reports']);

function isSupportMode(): boolean {
  return (
    localStorage.getItem('supportMode') === 'true' ||
    localStorage.getItem('isAgencyOwner') === 'true'
  );
}

export const AmpProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [premadeAmps, setPremadeAmps] = useState<Amp[]>([]);
  const [customAmps, setCustomAmps] = useState<Amp[]>([]);
  const [ampMessages, setAmpMessages] = useState<AmpMessages>({});
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [supportMode, setSupportMode] = useState<boolean>(isSupportMode);

  // Prevent duplicate fetch calls
  const fetchingPremadeRef = useRef(false);
  const fetchingCustomRef = useRef(false);

  useEffect(() => {
    const handler = () => setSupportMode(isSupportMode());
    window.addEventListener('supportModeChanged', handler);
    return () => window.removeEventListener('supportModeChanged', handler);
  }, []);

  const filteredPremadeAmps = useMemo(() =>
    supportMode ? premadeAmps : premadeAmps.filter((a) => !SUPPORT_ONLY_AMP_NAMES.has(a.name)),
  [premadeAmps, supportMode]);

  const amps = useMemo(() => {
    const all = [...filteredPremadeAmps, ...customAmps];
    return all;
  }, [filteredPremadeAmps, customAmps]);

  const fetchPremadeAmps = useCallback(async () => {
    if (fetchingPremadeRef.current) return;

    fetchingPremadeRef.current = true;
    try {
      const fetched = await ampService.getPremadeAmps();
      setPremadeAmps(fetched);
    } catch (err: any) {
      console.error('Failed to fetch premade AMPs:', err);
      setPremadeAmps([]);
    } finally {
      fetchingPremadeRef.current = false;
    }
  }, []);

  const fetchCustomAmps = useCallback(async () => {
    if (fetchingCustomRef.current) return;

    fetchingCustomRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const fetchedAmps = await ampService.getAllAmps();
      setCustomAmps(fetchedAmps);
    } catch (err: any) {
      console.error('Failed to fetch AMPs:', err);
      setError(err.message || 'Failed to fetch AMPs');
      setCustomAmps([]);
    } finally {
      setLoading(false);
      fetchingCustomRef.current = false;
    }
  }, []);

  // On-demand loading: the lists used to load on every page at app start, though
  // most pages never use them. Now the first screen that needs them calls
  // ensureAmpsLoaded() (directly, or via useAmpsLoaded); the result is kept for
  // the session and concurrent callers share one request.
  const loadedRef = useRef(false);
  const loadPromiseRef = useRef<Promise<void> | null>(null);

  const ensureAmpsLoaded = useCallback((): Promise<void> => {
    if (loadedRef.current) return Promise.resolve();
    if (loadPromiseRef.current) return loadPromiseRef.current;
    if (!localStorage.getItem('authToken')) {
      setInitialLoading(false);
      return Promise.resolve();
    }
    const p = Promise.all([fetchPremadeAmps(), fetchCustomAmps()])
      .then(() => { loadedRef.current = true; })
      .catch((err) => { console.error('Failed to load AMPs:', err); })
      .finally(() => {
        setInitialLoading(false);
        if (loadPromiseRef.current === p) loadPromiseRef.current = null;
      });
    loadPromiseRef.current = p;
    return p;
  }, [fetchPremadeAmps, fetchCustomAmps]);

  // A login (or SSO token refresh) only reloads lists that were already loaded
  // for the previous session; otherwise the next screen that needs them loads
  // them once — this also avoids the old double load right after login.
  useEffect(() => {
    const handleAuthTokenUpdate = () => {
      if (!loadedRef.current && !loadPromiseRef.current) return;
      loadedRef.current = false;
      void ensureAmpsLoaded();
    };
    window.addEventListener('authTokenUpdated', handleAuthTokenUpdate);
    return () => window.removeEventListener('authTokenUpdated', handleAuthTokenUpdate);
  }, [ensureAmpsLoaded]);

  const getAmp = useCallback(
    (id: string): Amp | undefined => {
      return amps.find((a) => a.id === id);
    },
    [amps]
  );

  const getAmpMessages = useCallback(
    (ampId: string): ChatMessage[] => {
      if (!ampMessages[ampId]) {
        return getDefaultAmpMessages(amps.find((a) => a.id === ampId), ampId);
      }
      return ampMessages[ampId];
    },
    [ampMessages, amps]
  );

  const addMessage = useCallback((ampId: string, message: ChatMessage) => {
    setAmpMessages((prev) => {
      const existing = prev[ampId] || getDefaultAmpMessages(amps.find((a) => a.id === ampId), ampId);
      return {
        ...prev,
        [ampId]: [...existing, message],
      };
    });
  }, [amps]);

  const addCustomAmp = useCallback((amp: Amp) => {
    setCustomAmps((prev) => [...prev, amp]);
  }, []);

  const removeCustomAmp = useCallback((ampId: string) => {
    setCustomAmps((prev) => prev.filter((a) => a.id !== ampId));
    setAmpMessages((prev) => {
      const { [ampId]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const createAmp = useCallback(async (amp: Partial<Amp>): Promise<Amp> => {
    setLoading(true);
    setError(null);
    try {
      const createdAmp = await ampService.createAmp(amp);
      setCustomAmps((prev) => [...prev, createdAmp]);
      return createdAmp;
    } catch (err: any) {
      console.error('Failed to create AMP:', err);
      setError(err.message || 'Failed to create AMP');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateAmp = useCallback(async (id: string, amp: Partial<Amp>): Promise<Amp> => {
    setLoading(true);
    setError(null);
    try {
      const updatedAmp = await ampService.updateAmp(id, amp);
      setCustomAmps((prev) => prev.map((a) => (a.id === id ? updatedAmp : a)));
      return updatedAmp;
    } catch (err: any) {
      console.error('Failed to update AMP:', err);
      setError(err.message || 'Failed to update AMP');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteAmp = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await ampService.deleteAmp(id);
      removeCustomAmp(id);
    } catch (err: any) {
      console.error('Failed to delete AMP:', err);
      setError(err.message || 'Failed to delete AMP');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [removeCustomAmp]);

  const value: AmpContextValue = {
    amps,
    premadeAmps: filteredPremadeAmps,
    customAmps,
    loading,
    initialLoading,
    error,
    getAmp,
    getAmpMessages,
    addMessage,
    createAmp,
    updateAmp,
    deleteAmp,
    fetchPremadeAmps,
    fetchCustomAmps,
    ensureAmpsLoaded,
    addCustomAmp,
    removeCustomAmp,
  };

  return <AmpContext.Provider value={value}>{children}</AmpContext.Provider>;
};
